import React, { useState, useEffect, useContext, useMemo } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import bloqueosContext from "../../context/bloqueos/bloqueosContext";
import authContext from "../../context/authentication/authContex";
import BloqueosActivos from "./BloqueosActivos";

const ModalGestionBloqueos = ({ show, onHide, canchas, onBloqueoCreado }) => {
  const { usuario } = useContext(authContext);
  /*
    saving (no loading): el boton solo debe deshabilitarse mientras hay una escritura en
    curso. loading corresponde a la recarga del listado, que arranca al abrir el modal y
    no tiene nada que ver con poder enviar el formulario.
  */
  const {
    saving,
    obtenerBloqueosActivos,
    crearBloqueo,
    crearBloqueosMultiples,
  } = useContext(bloqueosContext);

  // Estado del formulario
  const [canchasSeleccionadas, setCanchasSeleccionadas] = useState([]);
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [horaInicio, setHoraInicio] = useState("");
  const [horaFin, setHoraFin] = useState("");
  const [tipo, setTipo] = useState("OTRO");
  const [descripcion, setDescripcion] = useState("");
  const [bloquearDiaCompleto, setBloquearDiaCompleto] = useState(true);
  const [errorForm, setErrorForm] = useState(null);

  // Set para lookups O(1) al renderizar el listado de canchas (evita includes() O(n) por cancha)
  const seleccionadasSet = useMemo(
    () => new Set(canchasSeleccionadas),
    [canchasSeleccionadas]
  );

  // Cargar bloqueos activos al abrir el modal y resetear form al cerrar
  useEffect(() => {
    if (show) {
      obtenerBloqueosActivos();
    } else {
      setCanchasSeleccionadas([]);
      setFechaInicio("");
      setFechaFin("");
      setHoraInicio("");
      setHoraFin("");
      setTipo("OTRO");
      setDescripcion("");
      setBloquearDiaCompleto(true);
      setErrorForm(null);
    }
  }, [show]);

  // Manejar selección de canchas
  const handleCanchaToggle = (canchaId) => {
    if (canchasSeleccionadas.includes(canchaId)) {
      setCanchasSeleccionadas(canchasSeleccionadas.filter((id) => id !== canchaId));
    } else {
      setCanchasSeleccionadas([...canchasSeleccionadas, canchaId]);
    }
  };

  const handleSelectAll = () => {
    if (canchasSeleccionadas.length === canchas.length) {
      setCanchasSeleccionadas([]);
    } else {
      setCanchasSeleccionadas(canchas.map((c) => c._id));
    }
  };

  // Validar formulario
  const validarFormulario = () => {
    if (canchasSeleccionadas.length === 0) {
      setErrorForm("Debes seleccionar al menos una cancha");
      return false;
    }

    if (!fechaInicio || !fechaFin) {
      setErrorForm("Debes seleccionar fechas de inicio y fin");
      return false;
    }

    const inicio = new Date(fechaInicio);
    const fin = new Date(fechaFin);

    if (inicio > fin) {
      setErrorForm("La fecha de inicio no puede ser posterior a la fecha de fin");
      return false;
    }

    if (!bloquearDiaCompleto && (!horaInicio || !horaFin)) {
      setErrorForm("Debes seleccionar horario de inicio y fin");
      return false;
    }

    if (!bloquearDiaCompleto && horaInicio >= horaFin) {
      setErrorForm("La hora de inicio debe ser anterior a la hora de fin");
      return false;
    }

    if (!descripcion.trim()) {
      setErrorForm("Debes ingresar una descripción");
      return false;
    }

    return true;
  };

  // Crear bloqueo(s)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorForm(null);

    if (!validarFormulario()) {
      return;
    }

    if (tipo === "OTRO" && descripcion.trim().length > 15) {
      setErrorForm("Cuando el motivo es 'Otro', la descripción no puede superar los 15 caracteres para su correcta visualización en los slots");
      return;
    }

    try {
      const configBloqueo = {
        fechaInicio: fechaInicio + "T00:00:00.000Z",
        fechaFin: fechaFin + "T23:59:59.999Z",
        tipo,
        descripcion,
      };

      // Agregar horarios si no es día completo
      if (!bloquearDiaCompleto) {
        configBloqueo.horaInicio = horaInicio;
        configBloqueo.horaFin = horaFin;
      }

      // Si es una sola cancha, crear un bloqueo
      if (canchasSeleccionadas.length === 1) {
        const cancha = canchas.find((c) => c._id === canchasSeleccionadas[0]);
        const bloqueoData = {
          activoID: cancha._id,
          codActivo: cancha.codActivo,
          nameActivo: cancha.name,
          ...configBloqueo,
        };
        await crearBloqueo(bloqueoData);
      } else {
        // Si son múltiples canchas, usar la función de bloqueos múltiples
        const canchasABloquear = canchas.filter((c) =>
          canchasSeleccionadas.includes(c._id)
        );
        await crearBloqueosMultiples(canchasABloquear, configBloqueo);
      }

      // Limpiar formulario
      setCanchasSeleccionadas([]);
      setFechaInicio("");
      setFechaFin("");
      setHoraInicio("");
      setHoraFin("");
      setTipo("OTRO");
      setDescripcion("");
      setBloquearDiaCompleto(true);

      // Recargar bloqueos y slots
      obtenerBloqueosActivos();
      if (onBloqueoCreado) {
        onBloqueoCreado();
      }
    } catch (error) {
      console.error("Error al crear bloqueo:", error);
      setErrorForm(error.response?.data?.message || "Error al crear el bloqueo");
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static" style={{ zIndex: 9999 }} backdropClassName="modal-backdrop-high-z">
      <Modal.Body style={{ position: 'relative' }}>
        <button className="minimizarBtn" onClick={onHide} style={{ position: 'absolute', top: 5, right: 5, zIndex: 1 }}>X</button>
        <h4 style={{ marginBottom: '1rem' }}>Gestión de Bloqueos</h4>
        {/* Formulario de creación */}
        <div className="mb-4">
          <h5 className="mb-3">Crear Nuevo Bloqueo</h5>
          <Form onSubmit={handleSubmit}>
            {/* Selección de canchas */}
            <Form.Group className="mb-3">
              <Form.Label>Canchas a bloquear</Form.Label>
              <div className="border rounded p-3" style={{ maxHeight: "150px", overflowY: "auto" }}>
                <Form.Check
                  type="checkbox"
                  label="Seleccionar todas"
                  checked={canchasSeleccionadas.length === canchas.length}
                  onChange={handleSelectAll}
                  className="mb-2 fw-bold"
                />
                <hr />
                {canchas.map((cancha) => (
                  <Form.Check
                    key={cancha._id}
                    type="checkbox"
                    label={`${cancha.name} (${cancha.codActivo})`}
                    checked={seleccionadasSet.has(cancha._id)}
                    onChange={() => handleCanchaToggle(cancha._id)}
                    className="mb-1"
                  />
                ))}
              </div>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha Inicio</Form.Label>
                  <Form.Control
                    type="date"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha Fin</Form.Label>
                  <Form.Control
                    type="date"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Opción día completo */}
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Bloquear día completo"
                checked={bloquearDiaCompleto}
                onChange={(e) => setBloquearDiaCompleto(e.target.checked)}
              />
            </Form.Group>

            {/* Horarios específicos */}
            {!bloquearDiaCompleto && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Hora Inicio</Form.Label>
                    <Form.Control
                      type="time"
                      value={horaInicio}
                      onChange={(e) => setHoraInicio(e.target.value)}
                      required={!bloquearDiaCompleto}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Hora Fin</Form.Label>
                    <Form.Control
                      type="time"
                      value={horaFin}
                      onChange={(e) => setHoraFin(e.target.value)}
                      required={!bloquearDiaCompleto}
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Tipo de Bloqueo</Form.Label>
                  <Form.Select
                    value={tipo}
                    onChange={(e) => setTipo(e.target.value)}
                    required
                  >
                    <option value="MANTENIMIENTO">Mantenimiento</option>
                    <option value="EVENTO_PRIVADO">Evento Privado</option>
                    <option value="CLIMA">Clima</option>
                    <option value="CLASES">Clases</option>
                    <option value="OTRO">Otro</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Descripción</Form.Label>
                  <Form.Control
                    type="text"
                    name="descripcionBloqueo"
                    value={descripcion}
                    onChange={(e) => setDescripcion(e.target.value)}
                    placeholder="Motivo del bloqueo..."
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            {errorForm && (
              <div className="alert alert-danger">{errorForm}</div>
            )}

            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? "Creando..." : "Crear Bloqueo"}
            </Button>
          </Form>
        </div>

        <hr />

        <BloqueosActivos onBloqueoEliminado={onBloqueoCreado} />
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalGestionBloqueos;
